import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { PDFDocument, rgb } from 'pdf-lib';
import QRCode from 'qrcode';
import logo from './assets/logo.png';

// Helper Function to Center Text
const centerText = (text = '', fontSize = 14, pageWidth = 400, offset = 0) => {
  text = text || '';
  if (typeof text !== 'string') text = String(text);
  const textWidth = text.length * (fontSize * 0.5);
  return (pageWidth - textWidth) / 2 + offset; // Added offset parameter
};

// Helper function to wrap text
const wrapText = (text, maxWidth, fontSize) => {
  const words = text.split(' ');
  const lines = [];
  let currentLine = words[0];

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = (currentLine.length + word.length) * (fontSize * 0.5);
    if (width < maxWidth) {
      currentLine += ' ' + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  lines.push(currentLine);
  return lines;
};

export const generateTicketPDF = async (orderDetails) => {
  const {
    orderNumber = 'N/A',
    customerName = 'Guest',
    eventName = 'Unknown Event',
    ticketNumber = '0000',
    ticketType = 'General Admission',
    eventLocation = { location: 'Venue TBD', datetime: 'Date & Time TBD' },
  } = orderDetails;

  const pdfDoc = await PDFDocument.create();
  const page = pdfDoc.addPage([400, 600]);
  const { width, height } = page.getSize();

  const safeEventName = eventName ? String(eventName) : 'Event Name TBD';
  const safeCustomerName = customerName ? String(customerName) : 'Guest';
  const safeTicketType = ticketType ? String(ticketType) : 'General Admission';
  const safeTicketNumber = ticketNumber ? String(ticketNumber) : '0000';

  // Extracting location and datetime from the eventLocation object
  const safeEventLocation =
    typeof eventLocation === 'object'
      ? eventLocation.location || 'Venue TBD'
      : eventLocation || 'Venue TBD';
  const safeDatetime = orderDetails.datetime || 'Date & Time TBD';

  // Draw wrapped event name
  const eventNameLines = wrapText(safeEventName, width - 40, 14); // Changed from 18 to 14
  let yOffset = 0;
  for (const line of eventNameLines) {
    page.drawText(line, {
      x: centerText(line, 14, width, 10), // Added 10px right offset
      y: height - 50 - yOffset,
      size: 14, // Changed from 18 to 14
      color: rgb(0, 0, 0),
    });
    yOffset += 20; // Reduced from 25 to 20 for tighter line spacing
  }

  // Calculate starting position for remaining content
  const contentStartY = height - 50 - yOffset - 20;

  page.drawText('Location:', {
    x: centerText('Location:', 14, width),
    y: contentStartY,
    size: 14,
    color: rgb(0, 0, 0),
  });
  page.drawText(safeEventLocation, {
    x: centerText(safeEventLocation, 14, width),
    y: contentStartY - 20,
    size: 14,
    color: rgb(0, 0, 0),
  });

  page.drawText('Date & Time:', {
    x: centerText('Date & Time:', 14, width),
    y: contentStartY - 50,
    size: 14,
    color: rgb(0, 0, 0),
  });
  page.drawText(safeDatetime, {
    x: centerText(safeDatetime, 14, width),
    y: contentStartY - 70,
    size: 14,
    color: rgb(0, 0, 0),
  });

  page.drawText(safeCustomerName, {
    x: centerText(safeCustomerName, 14, width),
    y: contentStartY - 100,
    size: 14,
    color: rgb(0, 0, 0),
  });

  const ticketTypeLines = wrapText(
    `Ticket Type: ${safeTicketType}`,
    width - 40,
    12
  ); // Smaller font size (12)
  let ticketTypeY = contentStartY - 130;
  for (const line of ticketTypeLines) {
    page.drawText(line, {
      x: centerText(line, 12, width), // Note the 12px font size
      y: ticketTypeY,
      size: 12, // Smaller font size
      color: rgb(0, 0, 0),
    });
    ticketTypeY -= 15; // Adjust line spacing for smaller font
  }

  page.drawText(`Ticket #: ${safeTicketNumber}`, {
    x: centerText(`Ticket #: ${safeTicketNumber}`, 14, width),
    y: height / 2 - 100 - 30, // 30px below QR code
    size: 14,
    color: rgb(0, 0, 0),
  });
  const qrDataURL = await QRCode.toDataURL(safeTicketNumber, {
    errorCorrectionLevel: 'H',
  });
  const qrImageBytes = await fetch(qrDataURL).then((res) => res.arrayBuffer());
  const qrImage = await pdfDoc.embedPng(qrImageBytes);

  const logoBytes = await fetch(logo).then((res) => res.arrayBuffer());
  const logoImage = await pdfDoc.embedPng(logoBytes);

  page.drawImage(qrImage, {
    x: width / 2 - 75,
    y: height / 2 - 115, // Changed from -150 to -100 (moved up)
    width: 150,
    height: 150,
  });

  page.drawImage(logoImage, {
    x: width / 2 - 15,
    y: height / 2 - 100 + 45, // Center of QR code
    width: 30,
    height: 30,
  });

  page.drawText(`© 2025 Black Canyon Tickets`, {
    x: centerText(`© 2025 Black Canyon Tickets`, 10, width),
    y: 20,
    size: 10,
    color: rgb(0, 0, 0),
  });

  // Add disclosure statement
  const disclosureText =
    'By use of this ticket, the ticket purchaser/holder agrees that: (a) he or she shall not transmit or aid in transmitting any information about the event to which it grants admission, including, but not limited to, any account, description, picture, video, audio, reproduction, or other information concerning the event; and the event organizer and each of its respective agents, shall have the unrestricted right and license to use his or her image, likeness, name, voice, comments, or other proprietary or public rights and that of any minor accompanying the holder in any broadcast, telecast, or photograph and/or video and/or audio sound recording taken in connection with the event or other transmission or reproduction in whole or in part of the event, for all purposes, without compensation. The holder voluntarily assumes all risks incident to the event, including the risk of lost, stolen or damaged property or personal injury. The event organizer may revoke this license and eject or refuse entry to the holder for violation of venue rules, illegal activity, misconduct or failure to comply with any and all security measures (including metal detection and bag checks). Any item(s) determined to be unacceptable by the event organizer will not be permitted. If any prohibited items are revealed, the holder will be permitted to return to their vehicle with the item(s) or they can dispose of the item(s). The event organizer will not store any personal belongings or items of any type. The holder voluntarily assumes all risk associated with the purchase of this ticket from anyone other than the event organizer or its designated agents. This ticket may not be used for advertising, promotion (including contests or sweepstakes), or other trade or commercial purposes without the express written consent of the event organizer.';
  page.drawText(disclosureText, {
    x: 20,
    y: 110,
    size: 5,
    color: rgb(0.5, 0.5, 0.5),
    lineHeight: 7,
    maxWidth: width - 40,
  });

  const pdfBytes = await pdfDoc.save();
  return new Blob([pdfBytes], { type: 'application/pdf' });
};
